import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from "react-router-dom";
import toast from 'react-hot-toast';
import { 
  useTheme, 
  useMediaQuery, 
  Box,   
  Typography, 
  CircularProgress, 
  Tooltip, 
  IconButton, 
} from '@mui/material';
import { 
  AddCircleOutline, 
  Edit,
  Delete,
  DeleteForever,
  Download,
} from '@mui/icons-material';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import api from 'state/axios';
import {
  capitalizeFirstLetter,
  formatNumber,
  formatTZDate,
  getSelectedIds,
  isAnyRowSelected,
} from 'state/helpers';
import { exportToExcel } from 'state/excel';
import { logClientAction } from 'state/logger';
import { buildPermissionMap, hasRight } from 'state/checkPermission';
import DataGridComponent from 'components/DataGridComponent';
import Header from 'components/Header';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import StyledButton from 'components/StyledButton';
import DeleteConfirm from 'components/DeleteConfirm';
import CardList from 'components/CardList';


const TravelAuthorizationForms = () => {
  useAuthCheck();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user); 
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { startDate, endDate } = useDateRangeContext();

  const permissionMap = useMemo(
    () => buildPermissionMap(user?.permissions),
    [user?.permissions]
  );
  const canCreate = hasRight(permissionMap, 'TravelAuthorizationForms', 'create', false);
  const canEdit   = hasRight(permissionMap, 'TravelAuthorizationForms', 'update', false);
  const canDelete = hasRight(permissionMap, 'TravelAuthorizationForms', 'delete', false);
  const canExport = hasRight(permissionMap, 'TravelAuthorizationForms', 'export', false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const [rowSelectionModel, setRowSelectionModel] = useState({
    type: 'include',
    ids: new Set(),
  });
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [deleteType, setDeleteType] = useState(null); // 'single' or 'bulk'
  const [idToDelete, setIdToDelete] = useState(null);

  const [travelAuthorizationForms, setTravelAuthorizationForms] = useState([]);
  
  
  useEffect(() => {
    const handleTravelAuthorizationForms = async () => {
      try {
        if (!startDate || !endDate) return [];

        setError(null);
        setLoading(true);  

        const url = '/travelauthorizationforms';
        const payload = { startDate, endDate };
        const res = await api.post(url, payload);
  
        setTravelAuthorizationForms(res.data);
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    };
  
    handleTravelAuthorizationForms();
  }, [startDate, endDate]); 


  const columns = useMemo(() => [
    {
      field: "no",
      headerName: "No",
      filterable: false,      
      sortable: false,
      width: 60,
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,      
    },
    { 
      field: "crewCode", 
      headerName: "Code", 
      width: 60,
    },
    { 
      field: "crewName", 
      headerName: "CrewName", 
      width: 250,
    },    
    { 
      field: "jobGradeCode", 
      headerName: "JobGrade", 
      width: 80,
    },
    {
      field: "destination",
      headerName: "Destination",
      width: 90,
    }, 
    {
      field: "travelZone",
      headerName: "TravelZone",
      width: 90,
    },
    {
      field: "allowanceUnit",
      headerName: "AllowanceUnit",
      width: 100,
      renderCell: (params) => {       
        return (
          <Box
            sx={{
              height: '100%',
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
            }}
          >
            <Typography variant="body2">
              {formatNumber(params.value, 0, 0)}
            </Typography>            
          </Box>
        );
      }, 
    }, 
    {
      field: "startDate",
      headerName: "StartDate",
      align: "center",
      width: 120,
      valueFormatter: (value) => {
        if (value == null) {
          return '';
        }
        return `${formatTZDate(value, 'UTC', "HH:mm dd/MM/yyyy")}`;
      },
    },
    {
      field: "endDate",
      headerName: "EndDate",
      align: "center",
      width: 120,
      valueFormatter: (value) => {
        if (value == null) {
          return '';
        }
        return `${formatTZDate(value, 'UTC', "HH:mm dd/MM/yyyy")}`;
      },
    },
    {
      field: "travelHours",
      headerName: "TravelHours",
      width: 90,
      renderCell: (params) => {        
        return (
          <Box
            sx={{
              height: '100%',
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
            }}
          >
            <Typography variant="body2" sx={{ textAlign: "right" }}>
              {formatNumber(params.value, 0, 1)}
            </Typography>            
          </Box>
        );
      },      
    },  
    {
      field: "allowanceAmount",
      headerName: "AllowanceAmount",
      width: 120,
      renderCell: (params) => {       
        return (
          <Box
            sx={{
              height: '100%',
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
            }}
          >
            <Typography variant="body2">
              {formatNumber(params.value, 0, 0)}
            </Typography>            
          </Box>
        );
      }, 
    },    
    {
      field: "description",
      headerName: "Description",
      width: 300,
    },
    {
      field: "status",
      headerName: "Status",
      width: 90,    
      renderCell: (params) => {       
        return (
          <Box
            sx={{
              height: '100%',
              width: "100%",
              display: "flex",
              alignItems: "center",
            }}
          >
            <Typography variant="body2">
              {params.value ? capitalizeFirstLetter(params.value) : ''}
            </Typography>            
          </Box>
        );
      }, 
    },    
  ], []);

  const [columnVisibilityModel, setColumnVisibilityModel] = useState({ _id: false });

  const actionColumn = useMemo(() => {
    const activeCount = [canEdit, canDelete].filter(Boolean).length;
    if (activeCount === 0) return [];
    
    const baseWidth = 60 + activeCount * 25;

    return [
      {
        field: "action",
        headerName: "Action",
        filterable: false,
        sortable: false,
        width: baseWidth,
        renderCell: (params) => (
          <FlexCenter gap={1} sx={{ width: '100%', height: '100%' }}>
            {canEdit && (
              <Tooltip title="Edit">
                <IconButton
                  aria-label="Edit"
                  sx={{
                    color: theme.palette.primary.dark,
                    transition: 'color 0.3s ease',
                    '&:hover': { color: theme.palette.green.deep },
                  }}
                  onClick={() => navigate(`/travelauthorizationforms/${params.row._id}`)}
                >
                  <Edit />
                </IconButton>
              </Tooltip>
            )}
            {canDelete && (
              <Tooltip title="Delete">
                <IconButton
                  aria-label="Delete"
                  sx={{
                    color: theme.palette.primary.dark,
                    transition: 'color 0.3s ease',
                    '&:hover': { color: theme.palette.green.deep },
                  }}
                  onClick={() => handleDeleteClick(params.row._id)}
                >
                  <DeleteForever />
                </IconButton>
              </Tooltip>
            )}
          </FlexCenter>
        ),
      },
    ];
  }, [canEdit, canDelete, theme, navigate]);

  const handleDeleteClick = (id) => {
    setIdToDelete(id);
    setDeleteType('single');
    setConfirmDialogOpen(true);
  };

  const handleBulkDelete = () => {
    const selectedIds = getSelectedIds(rowSelectionModel, travelAuthorizationForms);    
    if (selectedIds.length === 0) {
      toast.error('No rows selected for deletion!');
      return;
    }

    setDeleteType('bulk');
    setConfirmDialogOpen(true);
  };


  const executeDelete = useCallback(async () => {
    try {
      if (!canDelete) return;

      if (deleteType === 'single') {
        if (!idToDelete) return;
        
        await api.delete(`/travelauthorizationforms/delete/${idToDelete}`);

        setTravelAuthorizationForms(travelAuthorizationForms.filter((item) => item._id !== idToDelete));
        toast.success('Travel authorization form deleted successfully.');
      } else if (deleteType === 'bulk') {
        const selectedIds = getSelectedIds(rowSelectionModel, travelAuthorizationForms);
        if (selectedIds.length === 0) {
          toast.error('No rows selected for deletion!');
          return;
        }
        const payload = { ids: selectedIds };
        await api.patch('/travelauthorizationforms/soft-delete', payload)

        setTravelAuthorizationForms(
          travelAuthorizationForms.filter(item => !selectedIds.includes(item._id))
        );
        toast.success(`${selectedIds.length} record(s) deleted successfully.`);
      }
    } catch (err) {
      toast.error(err.message || "Failed to delete!");
    }
  }, [
    canDelete,
    deleteType,
    idToDelete,
    rowSelectionModel,
    travelAuthorizationForms,
  ]);
 

  return (
    <Box className="main-container-box">
      <FlexBetween>
        <Header 
          title="Travel Authorization" 
          subtitle={`${loading ? 'Loading...' : 'List of Forms'}`}
        />
        {!loading && (
          <Box display="flex" alignItems="center" gap={1}>
            {canExport && (
              <Tooltip title="Download Excel file" arrow>
                <span>
                  <StyledButton
                    onClick={() => {
                      const omitFields = ['approvedBy', 'approvedAt', 'deletedAt', 'updatedAt', 'createdAt', 'createdBy', '__v'];
                      exportToExcel(travelAuthorizationForms, "TravelAuthorizationForms", omitFields);
                      logClientAction("EXPORT_REPORT", { fileName: "TravelAuthorizationForms", format: "Excel", startDate, endDate });
                    }}
                  >
                    <Download />
                  </StyledButton>
                </span>
              </Tooltip>
            )}
            {canDelete && (
              <Tooltip title="Bulk soft delete" arrow>
                <span>
                  <StyledButton
                    disabled={!isAnyRowSelected(rowSelectionModel, travelAuthorizationForms)}
                    onClick={handleBulkDelete}
                  >
                    <Delete />
                  </StyledButton>
                </span>
              </Tooltip>
            )}

            {canCreate && (
              <Tooltip title="Create" arrow>
                <span>
                  <StyledButton onClick={() => navigate("/travelauthorizationforms/new")}>
                    <AddCircleOutline />
                  </StyledButton>
                </span>
              </Tooltip>
            )}            
          </Box>
        )}
      </FlexBetween>

      {loading ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <CircularProgress />
        </FlexCenter>
      ) : error ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <Typography color="error">Error: {error?.message || ""}</Typography>
        </FlexCenter>
      ) : (
        <Box
          display="grid"
          gap={2}
          gridTemplateColumns="repeat(12, minmax(0, 1fr))"
        >
          <Box
            height={LAYOUT_HEIGHT}
            sx={{ gridColumn: "span 12", overflowY: 'auto' }}
          >
            {isMobile ? (
              <CardList
                rows={travelAuthorizationForms}
                primaryField="crewName"
                secondaryField="crewCode"
                fields={[
                  { label: "Job Grade", key: "jobGradeCode" },
                  { label: "Destination", key: "destination" },
                  { label: "Travel Zone", key: "travelZone" },
                  { label: "Allowance Unit", key: "allowanceUnit" },
                  { label: "Start Date", key: "startDate" },
                  { label: "End Date", key: "endDate" },
                  { label: "Travel Hours", key: "travelHours" },
                  { label: "Allowance Amount", key: "allowanceAmount" },
                  { label: "Status", key: "status" },
                  { label: "Description", key: "description" },
                ]}
                isActiveField={null}
                canEdit={canEdit}
                canDelete={canDelete}
                onEdit={(id) => navigate(`/travelauthorizationforms/${id}`)}
                onDelete={handleDeleteClick}
              />
            ) : (
              <DataGridComponent
                rows={travelAuthorizationForms || []}
                columns={columns.concat(actionColumn)}
                columnVisibilityModel={columnVisibilityModel}
                setColumnVisibilityModel={setColumnVisibilityModel}
                loading={loading}
                props={{
                  checkboxSelection: true,
                  disableRowSelectionOnClick: true,
                  initialState: {
                    pagination: { paginationModel: { pageSize: 100 } },
                  },
                  rowSelectionModel,
                  onRowSelectionModelChange: (newModel) => setRowSelectionModel(newModel),
                  getRowClassName: (params) =>
                    params.row.isActive === false ? 'isActive-false' : '',
                }}
                sx={{
                  '& .MuiDataGrid-row.isActive-false': {
                    backgroundColor: `${theme.palette.primary.light} !important`,
                  },
                }}
              />
            )}
          </Box>
        </Box>
      )}

      <DeleteConfirm
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={executeDelete}
        deleteType={deleteType}
      />
    </Box>
  );
};

export default TravelAuthorizationForms;
